import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, Fish } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { RigIntelligenceSurface } from "../components/RigIntelligenceSurface";
import { formatDateTime, totalWeightLabel, weightLabel } from "../lib/format";

export default function StatsPage() {
  const statsQuery = useQuery({
    queryKey: ["carpintel_stats"],
    queryFn: () =>
      fetch("/app-api/stats").then((r) => {
        if (!r.ok) throw new Error("Stats unavailable");
        return r.json();
      }),
  });
  const catchesQuery = useQuery({
    queryKey: ["carpintel_catches"],
    queryFn: () => fetch("/app-api/catches").then((r) => r.json()),
  });
  if (statsQuery.isLoading || catchesQuery.isLoading)
    return <PageMessage text="Building your insights…" />;
  if (statsQuery.error || catchesQuery.error)
    return (
      <PageMessage text="Could not load insights. Refresh and try again." />
    );

  const stats = statsQuery.data ?? {};
  const catches = catchesQuery.data ?? [];
  const personal = stats.my ?? stats;
  const species = personal.species ?? {};
  const summary = personal.summary ?? {
    total_catches: 0,
    biggest_fish_lbs: 0,
    biggest_species: null,
    biggest_lake_name: null,
    total_sessions: 0,
    sessions_with_catch: 0,
    catch_rate_pct: 0,
  };
  const biggestDetail = summary.biggest_species
    ? `${summary.biggest_species} at ${summary.biggest_lake_name}`
    : Number(summary.total_catches || 0) > 0
      ? "No weighed fish yet"
      : "Waiting for your first catch";

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <p className="text-sm font-medium text-secondary">
          What CatchCompass has learned from your fishing
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">
          My stats and insights
        </h1>
        <p className="mt-2 text-sm text-secondary">
          Everything on this page comes from your own logged fishing evidence.
          Community evidence has its own home in Community.
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-primary">My fishing</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            label="Total catches"
            value={summary.total_catches}
            detail="Your logged fish"
          />
          <StatCard
            label="Biggest fish"
            value={
              Number(summary.biggest_fish_lbs || 0) > 0
                ? totalWeightLabel(summary.biggest_fish_lbs)
                : "—"
            }
            detail={biggestDetail}
          />
          <StatCard
            label="Sessions"
            value={summary.total_sessions}
            detail={`${summary.sessions_with_catch} with a catch`}
          />
          <StatCard
            label="Carp & Cat Caught Sessions Catch Rate"
            value={`${Number(summary.catch_rate_pct || 0).toFixed(1)}%`}
            detail="Sessions where you caught at least one fish"
          />
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">By species</h2>
          <p className="mt-1 text-sm text-secondary">
            Catches show what you landed. Targeted-session catch rate only uses
            sessions where you were actually targeting that species, so an
            incidental catch can teach CatchCompass without pretending it was a
            targeted success.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SpeciesStats title="Carp" stats={species.carp} />
          <SpeciesStats title="Catfish" stats={species.catfish} />
        </div>
      </section>

      <RigIntelligenceSurface embedded />

      {/* Angler-sessions section — only shown when multi-angler data exists */}
      {Number(summary.total_angler_sessions || 0) >
      Number(summary.total_sessions || 0) ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">
              Angler-sessions
            </h2>
            <p className="mt-1 text-sm text-secondary">
              Each participating angler in one of your sessions counts as one
              angler-session. If two anglers fish one session, that counts as
              two angler-sessions.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard
              label="Total"
              value={Number(summary.total_angler_sessions || 0)}
              detail="Angler-sessions in your fishing history"
            />
            <StatCard
              label="Successful"
              value={Number(summary.successful_angler_sessions || 0)}
              detail="Your angler-sessions with a catch"
            />
            <StatCard
              label="Blank"
              value={Number(summary.blank_angler_sessions || 0)}
              detail="Your angler-sessions with no catch"
            />
            <StatCard
              label="Catch rate"
              value={`${Number(summary.angler_session_catch_rate_pct || 0).toFixed(1)}%`}
              detail="Your successful angler-sessions"
            />
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-primary">My best patterns</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Insight title="Best lake" result={personal.bestLake} />
          <Insight title="Best peg" result={personal.bestPeg} />
          <Insight title="Best bait" result={personal.bestBait} />
          <Insight title="Best rig" result={personal.bestRig} />
          <Insight
            title="Most productive time of day"
            result={personal.bestTime}
          />
          <Insight
            title="Most productive weather"
            result={personal.bestWeather}
          />
          <Insight
            title="Most productive moon phase"
            result={personal.bestMoon}
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-primary">My catch log</h2>
          <Button asChild size="sm">
            <Link to="/catches/new">
              <Fish /> Log catch
            </Link>
          </Button>
        </div>
        {catches.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <BarChart3 className="mx-auto text-secondary" />
            <p className="mt-3 text-sm text-secondary">
              Your private catches will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {catches.map((catchRow: any) => (
              <Link
                key={catchRow.id}
                to={`/catches/${catchRow.id}`}
                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border bg-raised p-3 transition-colors hover:bg-inset"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-primary">
                    {weightLabel(catchRow.weight_lbs, catchRow.weight_oz)} ·{" "}
                    {catchRow.species}
                  </p>
                  <p className="truncate text-xs text-secondary">
                    {catchRow.lake_name} · {catchRow.rig || "Rig not recorded"}{" "}
                    · {catchRow.main_bait || "Bait not recorded"}
                  </p>
                </div>
                <span className="shrink-0 text-right text-xs text-secondary">
                  {formatDateTime(catchRow.caught_at)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <p className="text-xs text-secondary">{label}</p>
      <p className="mt-2 truncate text-2xl font-semibold text-primary">
        {value}
      </p>
      <p className="mt-1 line-clamp-2 text-xs text-secondary">{detail}</p>
    </div>
  );
}

function SpeciesStats({ title, stats }: { title: string; stats: any }) {
  const targetedSessions = Number(stats?.targeted_sessions || 0);
  const successfulTargetedSessions = Number(
    stats?.successful_targeted_sessions || 0,
  );
  const catchRate =
    stats?.targeted_catch_rate_pct === null ||
    stats?.targeted_catch_rate_pct === undefined
      ? "No targeted sessions"
      : `${Number(stats.targeted_catch_rate_pct).toFixed(1)}%`;
  const biggest =
    Number(stats?.biggest_fish_lbs || 0) > 0
      ? totalWeightLabel(Number(stats.biggest_fish_lbs))
      : "—";

  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <h3 className="text-base font-semibold text-primary">{title}</h3>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <MiniStat label="Catches" value={Number(stats?.total_catches || 0)} />
        <MiniStat label="Biggest" value={biggest} />
        <MiniStat label="Targeted sessions" value={targetedSessions} />
        <MiniStat label="Targeted catch rate" value={catchRate} />
      </div>
      <p className="mt-3 text-xs leading-5 text-secondary">
        {targetedSessions > 0
          ? `${successfulTargetedSessions} successful and ${Number(stats?.blank_targeted_sessions || 0)} blank targeted session${targetedSessions === 1 ? "" : "s"}.`
          : Number(stats?.total_catches || 0) > 0
            ? `${Number(stats.total_catches)} catch observation${Number(stats.total_catches) === 1 ? "" : "s"} recorded, but no session was targeted at ${title.toLowerCase()}.`
            : `No ${title.toLowerCase()} evidence logged yet.`}
      </p>
    </div>
  );
}

function MiniStat({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-md bg-inset p-3">
      <p className="text-[11px] text-secondary">{label}</p>
      <p className="mt-1 text-sm font-semibold text-primary">{value}</p>
    </div>
  );
}

function Insight({ title, result }: { title: string; result: any }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-4">
      <p className="text-sm text-secondary">{title}</p>
      {result ? (
        <div className="mt-2 flex min-w-0 items-end justify-between gap-3">
          <p className="min-w-0 truncate text-base font-semibold text-primary">
            {result.label}
          </p>
          <span className="shrink-0 rounded-full bg-inset px-2 py-0.5 text-xs font-medium text-secondary">
            {result.catch_count}{" "}
            {Number(result.catch_count) === 1 ? "catch" : "catches"}
          </span>
        </div>
      ) : (
        <p className="mt-2 text-sm text-secondary">
          Log more catches to see this.
        </p>
      )}
    </div>
  );
}

function PageMessage({ text }: { text: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
      {text}
    </div>
  );
}
